// M13.1 — Source-mix section on /app/report. Sits between TimeSeriesSection
// and ExecutiveSummary in the dashboard scroll.
//
// Composition (top to bottom):
//   1. "Social share" KPI callout — sits ABOVE the section header. Stays
//      out of the main KpiGrid so the grid shape doesn't shift when
//      this property happens to have zero social sessions in the window.
//   2. Section header — eyebrow + accent bar + title (mirrors the rest
//      of ReportOverview's section pattern).
//   3. 100% normalised stacked area chart for the current period.
//   4. Optional second 100% stacked area for the compare period
//      (rendered when meta.compare !== "none").
//   5. Per-channel datatable showing all 15 GA4 channel buckets verbatim
//      (no Other rollup in the table — the chart owns that).

import { useMemo } from "react";
import type { ReportPayload, ReportSourceMix, ReportSourceMixChannelRow } from "@/lib/api";
import { SectionHeader } from "@/components/report/SectionHeader";
import { SourceMixChart } from "./SourceMixChart";
import { channelColor } from "./channel-colors";
import type { Granularity } from "./granularity";

export interface SourceMixSectionProps {
  sourceMix: ReportSourceMix;
  compare: ReportPayload["meta"]["compare"];
  granularity: Granularity;
}

export function SourceMixSection({ sourceMix, compare, granularity }: SourceMixSectionProps) {
  const compareActive = compare !== "none";
  const showCompareChart = compareActive && (sourceMix.trendCompare?.length ?? 0) > 0;

  const totals = useMemo(() => {
    let sessions = 0;
    let totalUsers = 0;
    let activeUsers = 0;
    let pageviews = 0;
    for (const r of sourceMix.perChannel) {
      sessions += r.sessions;
      totalUsers += r.totalUsers;
      activeUsers += r.activeUsers;
      pageviews += r.pageviews;
    }
    return { sessions, totalUsers, activeUsers, pageviews };
  }, [sourceMix.perChannel]);

  return (
    <section id="source-mix" className="scroll-mt-24 space-y-4">
      {/* "Social share" KPI callout — lives ABOVE the section header so
          the main KpiGrid stays stable across properties. */}
      <SocialShareCallout
        share={sourceMix.socialShare.sessions}
        delta={sourceMix.socialShare.delta}
        compareActive={compareActive}
      />

      <SectionHeader
        eyebrow="Source mix"
        title={
          <>
            The wider <em className="font-serif text-purple-700">channel mix</em> driving your site
          </>
        }
        description={
          <>
            GA4&rsquo;s default channel grouping across the whole site — not just social. The chart
            collapses everything past the top 8 channels into a grey &ldquo;Other&rdquo; band; the
            table below shows every bucket with absolute values + period-over-period delta.
          </>
        }
      />

      <div className="space-y-6 rounded-2xl bg-white p-5 ring-1 ring-neutral-950/5">
        <div>
          <p className="mb-3 text-[11px] font-bold uppercase tracking-wide text-neutral-500">
            Current period
          </p>
          <SourceMixChart
            rows={sourceMix.trend}
            granularity={granularity}
            ariaLabel="Source mix — current period — 100% stacked area"
          />
        </div>
        {showCompareChart ? (
          <div>
            <p className="mb-3 text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Comparison period
            </p>
            <SourceMixChart
              rows={sourceMix.trendCompare ?? []}
              granularity={granularity}
              ariaLabel="Source mix — compare period — 100% stacked area"
            />
          </div>
        ) : null}
      </div>

      <ChannelDataTable rows={sourceMix.perChannel} totals={totals} compareActive={compareActive} />
    </section>
  );
}

interface SocialShareCalloutProps {
  share: number;
  delta: number | null;
  compareActive: boolean;
}

function SocialShareCallout({ share, delta, compareActive }: SocialShareCalloutProps) {
  const pct = (share * 100).toFixed(1);
  const deltaPp = delta != null && Number.isFinite(delta) ? `${(delta * 100).toFixed(1)} pp` : null;
  const deltaTone =
    delta == null
      ? "text-neutral-500"
      : delta > 0
        ? "text-emerald-700"
        : delta < 0
          ? "text-rose-700"
          : "text-neutral-500";
  const arrow = delta == null ? "" : delta > 0 ? "▲" : delta < 0 ? "▼" : "·";

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-purple-200 bg-purple-50/60 px-5 py-4">
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wide text-purple-700">
          Social share
        </p>
        <p className="mt-1 font-display text-2xl font-semibold text-neutral-950">
          <span className="tabular-nums">{pct}%</span>
          <span className="ml-2 text-base font-medium text-neutral-600">of all sessions</span>
        </p>
        <p className="mt-1 max-w-[60ch] text-sm text-neutral-600 text-pretty">
          Organic Social + Paid Social as a share of every session GA4 saw on this property in the
          window.
        </p>
      </div>
      {compareActive && deltaPp ? (
        <div className="text-right">
          <p className="text-[11px] font-bold uppercase tracking-wide text-neutral-500">
            vs compare
          </p>
          <p className={`mt-1 font-display text-xl font-semibold tabular-nums ${deltaTone}`}>
            <span aria-hidden="true">{arrow}</span> {deltaPp}
          </p>
        </div>
      ) : compareActive ? (
        <div className="text-right">
          <p className="text-[11px] font-bold uppercase tracking-wide text-neutral-500">
            vs compare
          </p>
          <p className="mt-1 font-display text-xl font-semibold text-neutral-400">—</p>
        </div>
      ) : null}
    </div>
  );
}

interface ChannelDataTableProps {
  rows: ReportSourceMixChannelRow[];
  totals: { sessions: number; totalUsers: number; activeUsers: number; pageviews: number };
  compareActive: boolean;
}

function ChannelDataTable({ rows, totals, compareActive }: ChannelDataTableProps) {
  if (rows.length === 0) {
    return (
      <div className="rounded-2xl bg-white p-5 text-sm text-neutral-500 ring-1 ring-neutral-950/5">
        No channel data for this window.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-950/5">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 text-left">
            <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Channel
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Sessions
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Share
            </th>
            {compareActive ? (
              <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
                Δ Sessions
              </th>
            ) : null}
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Active users
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Total users
            </th>
            <th className="px-4 py-3 text-right text-[11px] font-bold uppercase tracking-wide text-neutral-500">
              Pageviews
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const share = totals.sessions > 0 ? r.sessions / totals.sessions : 0;
            return (
              <tr key={r.channel} className="border-b border-neutral-100 last:border-b-0">
                <td className="px-4 py-2.5">
                  <span className="inline-flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-block h-2.5 w-2.5 rounded-sm"
                      style={{ backgroundColor: channelColor(r.channel) }}
                    />
                    <span className="font-medium text-neutral-950">{r.channel}</span>
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-neutral-950">
                  {formatNumber(r.sessions)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                  {(share * 100).toFixed(1)}%
                </td>
                {compareActive ? (
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {renderDelta(r.deltas.sessions)}
                  </td>
                ) : null}
                <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                  {formatNumber(r.activeUsers)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                  {formatNumber(r.totalUsers)}
                </td>
                <td className="px-4 py-2.5 text-right tabular-nums text-neutral-700">
                  {formatNumber(r.pageviews)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat("en").format(Math.round(n));
}

function renderDelta(d: number | null): React.ReactNode {
  if (d == null || !Number.isFinite(d)) return <span className="text-neutral-400">—</span>;
  const pct = d * 100;
  if (Math.abs(pct) < 0.05) return <span className="text-neutral-500">0.0%</span>;
  const tone = pct > 0 ? "text-emerald-700" : "text-rose-700";
  const arrow = pct > 0 ? "▲" : "▼";
  return (
    <span className={tone}>
      {arrow} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}
